import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import MeetingCard from './components/MeetingCard';
import EventDetail from './components/EventDetail';
import SearchAndFilter from './components/SearchAndFilter';
import DocumentPage from './components/DocumentPage';
import LegislationPage from './components/legislation/LegislationPage';
import LegislationDetail from './components/legislation/LegislationDetail';
import { fetchCommitteeMeetings, fetchTrackedCommittees, searchEvents, meetingFromEvent, Meeting } from './services/legistarApi';
import './App.css';

// Navigation component
const Navigation: React.FC = () => {
  const location = useLocation();
  const isLegislation = location.pathname.startsWith('/legislation');
  const isMeetings = !isLegislation;

  return (
    <nav className="flex gap-1 mt-4">
      <Link
        to="/"
        className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
          isMeetings
            ? 'bg-white text-nyc-blue'
            : 'bg-nyc-blue/20 text-white hover:bg-nyc-blue/30'
        }`}
      >
        Meetings
      </Link>
      <Link
        to="/legislation"
        className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
          isLegislation
            ? 'bg-white text-nyc-blue'
            : 'bg-nyc-blue/20 text-white hover:bg-nyc-blue/30'
        }`}
      >
        Legislation
      </Link>
    </nav>
  );
};

interface Committee {
  id: number;
  name: string;
}

function App() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [filteredMeetings, setFilteredMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [committeeFilter, setCommitteeFilter] = useState<number | undefined>(undefined);
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [searchAttachments, setSearchAttachments] = useState(false);
  const [searchResults, setSearchResults] = useState<Meeting[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastMeetingRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const getFilteredMeetings = useCallback(async (pageNum: number, currentFilter: string, currentCommitteeFilter?: number) => {
    try {
      setLoading(true);
      const now = new Date();
      let startDate = '';
      let endDate = '';

      switch (currentFilter) {
        case 'upcoming':
          startDate = now.toISOString().split('T')[0];
          break;
        case 'past':
          endDate = now.toISOString().split('T')[0];
          break;
        case 'thisMonth':
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          startDate = firstDay.toISOString().split('T')[0];
          endDate = lastDay.toISOString().split('T')[0];
          break;
      }

      // Upcoming view reads soonest-first; everything else newest-first
      const order = currentFilter === 'upcoming' ? 'asc' : undefined;
      const newMeetings = await fetchCommitteeMeetings(pageNum, startDate, endDate, currentCommitteeFilter, order);
      if (newMeetings.length === 0) {
        setHasMore(false);
      } else {
        setMeetings(prev => pageNum === 0 ? newMeetings : [...prev, ...newMeetings]);
      }
    } catch (err) {
      setError('Failed to load meetings. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch tracked committees on mount
  useEffect(() => {
    const loadCommittees = async () => {
      try {
        const trackedCommittees = await fetchTrackedCommittees();
        setCommittees(trackedCommittees);
      } catch (err) {
        console.error('Failed to load committees:', err);
      }
    };
    loadCommittees();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      getFilteredMeetings(page, filter, committeeFilter);
    }
  }, [page, filter, committeeFilter, getFilteredMeetings, searchQuery]);

  // Server-side search when query is present
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setSearchResults(null);
      return;
    }

    const performSearch = async () => {
      setSearchLoading(true);
      try {
        const result = await searchEvents(searchQuery, {
          bodyId: committeeFilter,
          searchAttachments,
          pageSize: 20
        });

        // Convert server results to Meeting format. The search endpoint inlines
        // each result's doc-links + topics, so there's no per-result
        // /events/:id/items fetch anymore (codex PF2).
        setSearchResults(result.items.map(meetingFromEvent));
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    performSearch();
  }, [searchQuery, committeeFilter, searchAttachments]);

  // Client-side filtering only applies when no server search is active
  useEffect(() => {
    if (searchResults !== null) return;
    setFilteredMeetings([...meetings]);
  }, [meetings, searchResults]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setPage(0);
    setHasMore(true);
    setMeetings([]);
  };

  const handleCommitteeFilterChange = (committeeId: number | undefined) => {
    setCommitteeFilter(committeeId);
    setPage(0);
    setHasMore(true);
    setMeetings([]);
  };

  const handleSearchAttachmentsChange = useCallback((enabled: boolean) => {
    setSearchAttachments(enabled);
  }, []);

  // Determine which meetings to display
  const displayMeetings = searchResults !== null ? searchResults : filteredMeetings;
  const isSearchActive = searchResults !== null;

  // Subtitle derived from the tracked committees (drop the "Committee on" prefix)
  // so it never goes stale; falls back to the current two until they load.
  const committeeSubtitle =
    committees.length > 0
      ? committees.map((c) => c.name.replace(/^Committee on\s+/i, '')).join(' | ')
      : 'Public Housing | Environmental Protection and Waterfronts';

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <header className="bg-nyc-blue text-white pt-6">
          <div className="container mx-auto px-4">
            <Link to="/" className="text-4xl font-bold hover:text-nyc-orange transition-colors">
              NYC Council Committees
            </Link>
            <p className="mt-2 text-lg">{committeeSubtitle}</p>
            <Navigation />
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={
              <>
                <SearchAndFilter
                  onSearch={handleSearch}
                  onFilterChange={handleFilterChange}
                  onCommitteeFilterChange={handleCommitteeFilterChange}
                  onSearchAttachmentsChange={handleSearchAttachmentsChange}
                  committees={committees}
                  selectedCommittee={committeeFilter}
                  searchAttachments={searchAttachments}
                />
                {(searchLoading || (loading && page === 0)) && !isSearchActive ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nyc-blue mx-auto"></div>
                    <p className="mt-4 text-gray-600">{searchLoading ? 'Searching meetings...' : 'Loading meetings...'}</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600">{error}</p>
                  </div>
                ) : displayMeetings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">
                      {isSearchActive ? 'No meetings found matching your search.' : 'No meetings found matching your criteria.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {isSearchActive && (
                      <p className="text-sm text-gray-500 mb-4">
                        Found {displayMeetings.length} meeting{displayMeetings.length !== 1 ? 's' : ''} matching your search
                      </p>
                    )}
                    <div className="grid grid-cols-1 gap-8">
                      {displayMeetings.map((meeting, index) => (
                        <div
                          key={meeting.id}
                          ref={!isSearchActive && index === displayMeetings.length - 1 ? lastMeetingRef : null}
                        >
                          {/* Not wrapped in a Link: the card contains its own links
                              (bill pages, meeting details) — nested anchors are invalid */}
                          <MeetingCard
                            id={meeting.id}
                            title={meeting.title}
                            date={meeting.date}
                            rawDate={meeting.rawDate}
                            time={meeting.time}
                            location={meeting.location}
                            committee={meeting.committee}
                            comment={meeting.comment}
                            topics={meeting.topics}
                            documents={meeting.documents}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {loading && page > 0 && !isSearchActive && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nyc-blue mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading more meetings...</p>
                  </div>
                )}
              </>
            } />
            <Route path="/event/:eventId" element={<EventDetail />} />
            <Route path="/event/:eventId/document/:matterId/:attachmentId/:documentName" element={<DocumentPage />} />
            <Route path="/legislation" element={<LegislationPage />} />
            <Route path="/legislation/:matterId" element={<LegislationDetail />} />
            <Route path="/legislation/:matterId/document/:attachmentId/:documentName" element={<DocumentPage />} />
          </Routes>
        </main>
        <footer className="text-center py-4 text-gray-400 text-xs">
          {process.env.REACT_APP_GIT_SHA && <span>v:{process.env.REACT_APP_GIT_SHA}</span>}
        </footer>
      </div>
    </Router>
  );
}

export default App;
